import React, { ChangeEventHandler, memo } from 'react'
import times from 'lodash/times'
import { Skeleton } from 'transfix-ui/core'
import { KeepTruckinTruckLocations } from 'apps/fleet-planner/types'
import { Checkbox } from 'components/forms'
import { Table, TableBody, TableCell, TableHead, TableRow } from 'transfix-ui'
import { useImportTruckListFormContext } from './TruckListImportContext'

interface IImportedTruckListRow {
  truck: KeepTruckinTruckLocations
  checked: boolean
  onChange: ChangeEventHandler<HTMLInputElement>
}

const getMakeAndModel = (truck: KeepTruckinTruckLocations): string => {
  const { make, model } = truck || {}
  const makeName = make ?? ''
  const modelName = model ?? ''
  if (!makeName && !modelName) return '--'
  return `${makeName} ${modelName}`.trim()
}

const getDriverName = (truck: KeepTruckinTruckLocations): string => {
  const { currentDriver } = truck || {}
  const { firstName = '', lastName = '' } = currentDriver ?? {}
  if (!firstName && !lastName) return '--'
  return `${firstName} ${lastName}`.trim()
}

const ImportedTruckListRow = memo<IImportedTruckListRow>(({ checked, onChange, truck }) => {
  const { number: vehicleNumber } = truck || {}
  const makeAndModel = getMakeAndModel(truck)
  const driverName = getDriverName(truck)
  return (
    <TableRow data-test='trucks-table-row'>
      <TableCell data-test='trucks-table-row-checkbox'>
        <Checkbox onChange={onChange} checked={checked} />
      </TableCell>
      <TableCell data-test='trucks-table-row-vehicle'>{vehicleNumber ?? '--'}</TableCell>
      <TableCell data-test='trucks-table-row-make-model'>{makeAndModel}</TableCell>
      <TableCell data-test='trucks-table-row-driver'>{driverName}</TableCell>
    </TableRow>
  )
})

interface IImportedTruckListProps {
  isLoading: boolean
  trucks: KeepTruckinTruckLocations[]
}

/**
 * List of imported trucks where user can select which to use
 */
export const ImportedTruckList = memo<IImportedTruckListProps>(
  ({ trucks = [], isLoading = false }) => {
    const {
      allTrucksToggled,
      createChangeHandler,
      getSelectedByKeyName,
      toggleAllHandler,
    } = useImportTruckListFormContext()
    return (
      <Table dataTest='trucks-table'>
        <TableHead>
          <TableRow>
            <TableCell>
              <Checkbox onChange={toggleAllHandler} checked={allTrucksToggled} />
            </TableCell>
            <TableCell>Vehicle Number</TableCell>
            <TableCell>Make/Model</TableCell>
            <TableCell>Driver in KeepTruckin</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!isLoading &&
            trucks.map(truck => {
              const { id } = truck || {}
              if (!id) return null
              const changeHandler = createChangeHandler(String(id))
              const checked = getSelectedByKeyName(String(id))
              return (
                <ImportedTruckListRow
                  key={id}
                  onChange={changeHandler}
                  checked={checked}
                  truck={truck}
                />
              )
            })}
          {isLoading &&
            times(5).map(item => (
              <TableRow key={item} data-test='trucks-table-row-loading'>
                <TableCell data-test='trucks-table-row-checkbox'>
                  <Skeleton variant='rectangular' height='16px' width='16px' />
                </TableCell>
                <TableCell data-test='trucks-table-row-vehicle'>
                  <Skeleton variant='rectangular' height='22px' width='141px' />
                </TableCell>
                <TableCell data-test='trucks-table-row-driver'>
                  <Skeleton variant='rectangular' height='22px' width='141px' />
                </TableCell>
                <TableCell data-test='trucks-table-row-eld'>
                  <Skeleton variant='rectangular' height='22px' width='141px' />
                </TableCell>
              </TableRow>
            ))}
          {!isLoading && !trucks?.length && (
            <TableRow data-test='trucks-table-row-loading'>
              <TableCell data-test='trucks-table-row-checkbox' colSpan={4}>
                No available trucks to import
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    )
  }
)
